// De logica achter de skilleditor, zonder React: vitest draait het zonder DOM.
//
// De editor houdt een skill in de vorm van zijn invoervelden (opties als één
// regel tekst); `toPayload` maakt er de definitie van die de server controleert.
//
// Eén soort skill: de instructie bepaalt hoe hij draait. Zonder instructie voert
// hij precies één actie uit en komt het formulier uit die actie; met instructie
// heeft hij eigen velden en mag het model zijn acties gebruiken. Een actie zit in
// een skill als koppeling: per veld laten, voorvullen of vastzetten.

import { SkillField, SkillFieldType } from "../workspace/chat/types";

// Een actie in een skill: per veld een eigen default (voorgevuld, aan te passen)
// of een vaste waarde (niet in het formulier, niet voor het model).
export interface SkillBinding {
  action: string;
  defaults: Record<string, any>;
  fixed: Record<string, any>;
}

export interface LibrarySkill {
  // Leeg bij een systeemskill: die staat in code en is niet te bewerken.
  id?: number | null;
  name: string;
  label: string;
  when: string;
  instruction: string;
  fields: SkillField[];
  actions: SkillBinding[];
  // Een van zijn acties kost geld.
  paid?: boolean;
  system: boolean;
  createdBy?: string;
  updatedBy?: string;
  modified?: string | null;
}

export interface SkillAction {
  name: string;
  label: string;
  description: string;
  paid: boolean;
  // Gebruikt het antwoord van het model: alleen in een skill met instructie.
  needsAnswer: boolean;
  fields: SkillField[];
}

export type FieldMode = "keep" | "default" | "fixed";

export const FIELD_MODES: { value: FieldMode; label: string }[] = [
  { value: "keep", label: "Laten" },
  { value: "default", label: "Voorvullen" },
  { value: "fixed", label: "Vastzetten" },
];

export const fieldMode = (binding: SkillBinding, name: string): FieldMode =>
  name in binding.fixed ? "fixed" : name in binding.defaults ? "default" : "keep";

/** Een perceel hoort bij een project, een skill bij de organisatie. */
export const canAdjust = (field: SkillField) => field.type !== "parcel" && field.type !== "parcels";

// Waar een veld mee begint als je het voorvult of vastzet.
const startValue = (field: SkillField) => {
  if (field.default !== undefined && field.default !== null) return field.default;
  if (field.type === "multi_choice") return [];
  if (field.type === "choice") return field.options[0]?.value ?? "";
  if (field.type === "range") return {};
  return "";
};

const without = (values: Record<string, any>, name: string) =>
  Object.fromEntries(Object.entries(values).filter(([key]) => key !== name));

/**
 * Een veld laten, voorvullen of vastzetten. De waarde gaat mee van voorvullen
 * naar vastzetten en terug; wie een veld laat, raakt zijn waarde kwijt.
 */
export function setFieldMode(
  binding: SkillBinding,
  field: SkillField,
  mode: FieldMode
): SkillBinding {
  const current = binding.fixed[field.name] ?? binding.defaults[field.name] ?? startValue(field);
  const next = {
    ...binding,
    defaults: without(binding.defaults, field.name),
    fixed: without(binding.fixed, field.name),
  };
  if (mode === "default") next.defaults[field.name] = current;
  if (mode === "fixed") next.fixed[field.name] = current;
  return next;
}

/** De waarde van een voorgevuld of vastgezet veld wijzigen. */
export function setFieldValue(binding: SkillBinding, field: SkillField, value: any): SkillBinding {
  const mode = fieldMode(binding, field.name);
  if (mode === "keep") return binding;
  const key = mode === "fixed" ? "fixed" : "defaults";
  return { ...binding, [key]: { ...binding[key], [field.name]: value } };
}

/**
 * Wat het formulier van een skill zonder instructie vraagt: de velden van de
 * actie zonder de vastgezette, met de voorgevulde waarden als default. Zo
 * rekent de server het ook (`ActionBinding.open_fields`).
 */
export const openFields = (fields: SkillField[], binding: SkillBinding): SkillField[] =>
  fields
    .filter((field) => !(field.name in binding.fixed))
    .map((field) =>
      field.name in binding.defaults ? { ...field, default: binding.defaults[field.name] } : field
    );

export const emptyBinding = (action: string): SkillBinding => ({
  action,
  defaults: {},
  fixed: {},
});

export interface FieldDraft {
  // Alleen voor React: de naam verandert terwijl je het label typt.
  uid: number;
  name: string;
  label: string;
  type: SkillFieldType;
  required: boolean;
  // Keuzes, één per regel: een keuze mag zelf een komma bevatten.
  options: string;
  description: string;
}

export interface SkillDraft {
  id?: number;
  label: string;
  when: string;
  instruction: string;
  fields: FieldDraft[];
  actions: SkillBinding[];
}

export const INSTRUCTION_MAX = 4000;

export const FIELD_TYPES: { value: SkillFieldType; label: string }[] = [
  { value: "parcel", label: "Eén perceel" },
  { value: "parcels", label: "Meerdere percelen" },
  { value: "text", label: "Tekst" },
  { value: "choice", label: "Eén keuze" },
  { value: "multi_choice", label: "Meerdere keuzes" },
];

export const hasOptions = (type: SkillFieldType) => type === "choice" || type === "multi_choice";

/**
 * Een veldnaam uit een label: kleine letters, cijfers en _, beginnend met een
 * letter, en niet al in gebruik. De naam ziet alleen het model; de gebruiker
 * ziet het label.
 */
export function fieldName(label: string, taken: string[]): string {
  const base =
    label
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "")
      .replace(/^(?=[0-9])/, "veld_")
      .slice(0, 36) || "veld";
  let name = base;
  for (let number = 2; taken.includes(name); number++) name = `${base}_${number}`;
  return name;
}

let nextUid = 1;

export const emptyField = (taken: string[]): FieldDraft => ({
  uid: nextUid++,
  name: fieldName("veld", taken),
  label: "",
  type: "text",
  required: false,
  options: "",
  description: "",
});

const fieldDraft = (field: SkillField): FieldDraft => ({
  uid: nextUid++,
  name: field.name,
  label: field.label,
  type: field.type,
  required: field.required,
  options: field.options.map((option) => option.value).join("\n"),
  description: field.description,
});

const copyBinding = (binding: SkillBinding): SkillBinding => ({
  action: binding.action,
  defaults: { ...binding.defaults },
  fixed: { ...binding.fixed },
});

/**
 * De beginstand van de editor. Een eigen skill bewerk je; een systeemskill
 * dupliceer je als basis. Kavels zoeken en Kavelonderzoek worden zo een
 * voorinstelling (geen instructie, dezelfde actie, nog niets aangepast), het
 * Gespreksrapport krijgt zijn instructie mee. Bereikvelden vallen weg: die kent
 * een eigen veld niet.
 */
export function draftFrom(skill?: LibrarySkill): SkillDraft {
  if (!skill) return { label: "", when: "", instruction: "", fields: [], actions: [] };
  const fields = skill.fields
    .filter((field) => FIELD_TYPES.some((type) => type.value === field.type))
    .map(fieldDraft);
  return {
    id: skill.system ? undefined : (skill.id ?? undefined),
    label: skill.system ? `${skill.label} (kopie)` : skill.label,
    when: skill.when,
    instruction: skill.instruction,
    fields,
    actions: skill.actions.map(copyBinding),
  };
}

const splitOptions = (text: string) =>
  text
    .split("\n")
    .map((option) => option.trim())
    .filter(Boolean);

/**
 * De definitie voor de server; die controleert alles opnieuw. Zonder instructie
 * gaan er geen eigen velden mee: het formulier komt dan uit de actie.
 */
export function toPayload(draft: SkillDraft) {
  const instruction = draft.instruction.trim();
  return {
    label: draft.label.trim(),
    when: draft.when.trim(),
    instruction,
    fields: (instruction ? draft.fields : []).map((field) => ({
      name: field.name,
      label: field.label.trim(),
      type: field.type,
      required: field.required,
      options: hasOptions(field.type)
        ? splitOptions(field.options).map((value) => ({ value, label: value }))
        : [],
      description: field.description.trim(),
    })),
    actions: draft.actions,
  };
}
